import React from 'react';

import cn from 'classnames';
import { isString, noop } from 'lodash';

import {
  bool,
  func,
  shape,
  oneOf,
  string,
  number,
  oneOfType,
  element,
} from 'prop-types';

import FormControlLabel from '@mui/material/FormControlLabel';
import MuiRadio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';

import Flexbox from 'components/Common/Flexbox';
import Text from 'components/Common/Text';

import s from './Radio.css';

const styles = {
  small: s.small,
  medium: s.medium,
};

const Radio = ({
  name,
  size,
  label,
  value,
  checked,
  classes,
  disabled,
  onChange,
  hasBorder,
  hasError,
  errorText,
  description,
  radioClasses,
  removeOffset,
  containerClass,
}) => {
  const renderLabel = () => {
    if (!label) {
      return null;
    }

    if (isString(label)) {
      return (
        <Typography
          classes={{
            root: s.label,
          }}
        >
          {label}
        </Typography>
      );
    }

    return label;
  };

  return (
    <Flexbox flexDirection="column" flex={label || description ? 1 : null}>
      <FormControlLabel
        value={value}
        control={
          <MuiRadio
            name={name}
            size={size}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            classes={{
              root: s.radio,
              checked: !disabled && s.checked,
              ...radioClasses,
            }}
          />
        }
        label={
          <>
            {renderLabel()}
            {description ? (
              <Text variant="label" className={s.description}>
                {description}
              </Text>
            ) : null}
          </>
        }
        classes={{
          root: cn(
            containerClass,
            { [s.disabled]: disabled },
            s.root,
            styles[size],
            {
              [s.border]: hasBorder && label,
              [s.alignStart]: description,
              [s.checked]: checked,
              [s.error]: hasError,
              [s.removeOffset]: removeOffset,
            },
          ),
          label: s.labelDescription,
          ...classes,
        }}
      />
      {hasError && errorText ? (
        <Text variant="meta" color="redDark">
          {errorText}
        </Text>
      ) : null}
    </Flexbox>
  );
};

Radio.propTypes = {
  name: string,
  disabled: bool,
  onChange: func,
  hasError: bool,
  hasBorder: bool,
  errorText: string,
  removeOffset: bool,
  classes: shape({}),
  description: string,
  containerClass: string,
  radioClasses: shape({}),
  checked: bool.isRequired,
  size: oneOf(['small', 'medium']),
  label: oneOfType([string, element]),
  value: oneOfType([string, number]).isRequired,
};

Radio.defaultProps = {
  name: '',
  label: '',
  classes: {},
  size: 'medium',
  disabled: false,
  description: '',
  hasBorder: false,
  hasError: false,
  radioClasses: {},
  errorText: null,
  removeOffset: false,
  onChange: noop,
  containerClass: undefined,
};

export default Radio;
